import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import {
  AppUserService,
  AttachmentService,
  FlatRenterMappingService,
  PaymentService,
  RenterService,
  TokenStorageService
} from '../../core/services';
import { CommonDropdownComponent } from '../../core/ui/common-dropdown/common-dropdown.component';
import { ContentSkeletonComponent } from '../../core/ui/content-skeleton/content-skeleton.component';
import { EntityListComponent, EntityListMenuAction, TableHeaderAndValueKeySets } from '../../core/ui/entity-list/entity-list.component';
import {
  appendUniqueById,
  createListPaginationState,
  hasMoreByTotal,
  resolveRequestPage,
  resetListPaginationState,
  updateAfterFetch
} from '../../core/utils/list-pagination.util';
import { AddEditRenterComponent, RenterSaveEvent } from './add-edit-renter/add-edit-renter.component';
import {
  APP_DEVELOPED_BY,
  ATTACHMENT_ALLOWED_EXTENSIONS_BY_FEATURE,
  ATTACHMENT_MAX_FILE_COUNT_BY_FEATURE,
  ATTACHMENT_MAX_FILE_SIZE_BYTES,
  FeatureTypeEnum
} from '../../core/constants/app-constants';
import { MONTH_TRANSLATION_KEYS } from '../../core/i18n/app-translations';
import { TranslatePipe } from '../../core/pipes/translate.pipe';
import { LanguageService } from '../../core/services/language.service';
import {
  downloadRenterReportExcel,
  downloadRenterReportPdf,
  RenterReportData
} from './renter-details-report.util';
import { ConfirmActionModalComponent } from '../../core/ui/confirm-action-modal/confirm-action-modal.component';
import { SubscriptionService, SubscriptionUsageSummary } from '../../core/services/subscription.service';

interface RenterAttachment {
  id: number;
  attachmentFileName?: string;
  attachmentUrl?: string;
  createdOn?: string;
}

interface Renter {
  id: number;
  renterName: string;
  renterMobile: string;
  renterEmail?: string;
  renterUsername?: string;
  renterPhotoIdNo?: string;
  renterPermanentAddress?: string;
  renterPreviousAddress?: string;
  status: number;
  createdOn?: string;
  updatedOn?: string | null;
  attachments?: RenterAttachment[];
}

interface ApiResponse {
  code: string;
  message: string;
  totalRows: number;
  data: Renter[];
}

interface RenterRow extends Renter {
  nameTopLine: string;
  nameBottomLine: string;
  statusLabelText: string;
}

interface RenterMappingInfo {
  id: number;
  status?: number;
  renterStartDate?: string;
  renterEndDate?: string;
  advanceAmount?: number;
  usedAdvanceAmount?: number;
  renter?: {
    id?: number;
    renterName?: string;
    renterMobile?: string;
    renterEmail?: string;
  };
  flat?: {
    id?: number;
    flatName?: string;
    floorName?: string;
    negativeChargeName?: string;
    house?: {
      id?: number;
      houseName?: string;
      houseNo?: string;
      houseAddress?: string;
    };
  };
  createdOn?: string;
  updatedOn?: string;
  attachments?: RenterAttachment[];
}

interface RenterPaymentItem {
  id: number;
  paymentMonth?: number;
  paymentYear?: number | string;
  paymentDate?: string;
  rentAmount?: number;
  gasBillAmount?: number;
  electricBillAmount?: number;
  waterBillAmount?: number;
  serviceChargeAndOtherAmount?: number;
  negativeChargeAmount?: number;
  advanceAdjustedAmount?: number;
  createdOn?: string;
  status?: number;
}

interface RenterPaymentRow {
  monthLabel: string;
  paymentDateLabel: string;
  rentLabel: string;
  gasLabel: string;
  electricLabel: string;
  waterLabel: string;
  serviceLabel: string;
  otherLabel: string;
  advanceAdjustedLabel: string;
  totalLabel: string;
}

interface RenterFlatOption {
  value: number;
  label: string;
}

@Component({
  selector: 'app-renter-list',
  standalone: true,
  imports: [CommonModule, MatSnackBarModule, EntityListComponent, AddEditRenterComponent, CommonDropdownComponent, ContentSkeletonComponent, ConfirmActionModalComponent, TranslatePipe],
  templateUrl: './renter-list.component.html',
  styleUrls: ['./renter-list.component.css', './../../../CSSFiles/BTN_Style.css']
})
export class RenterListComponent implements OnInit, OnDestroy {
  @Output() postCreateUploadFinished = new EventEmitter<void>();
  @ViewChild('cropViewport') cropViewportRef?: ElementRef<HTMLDivElement>;
  private readonly mappingFilterKeys = ['flat.house.id', 'flat.floorName', 'flat.id'] as const;
  isAuthenticated = false;
  isLoading = false;
  isLoadingMore = false;
  renterList: RenterRow[] = [];
  subscriptionSummary: SubscriptionUsageSummary | null = null;
  error: string | null = null;
  status: string | null = null;
  filters: Record<string, any> = {};
  private readonly paginationState = createListPaginationState();
  private summaryRefreshSubscription?: Subscription;

  selectedRenter: Renter | null = null;
  pendingDeleteRenter: Renter | null = null;
  pendingRestoreRenter: Renter | null = null;
  selectedRenterMapping: RenterMappingInfo | null = null;
  renterMappings: RenterMappingInfo[] = [];
  renterFlatOptions: RenterFlatOption[] = [];
  selectedFlatId: number | null = null;
  selectedPaymentYear = `${new Date().getFullYear()}`;
  readonly detailsYearOptions = this.buildDetailsYearOptions();
  renterPaymentItems: RenterPaymentItem[] = [];
  renterPaymentRows: RenterPaymentRow[] = [];
  isDetailsDataLoading = false;
  detailsDataError: string | null = null;
  isDetailsOpen = false;
  isEditOpen = false;
  isDeleting = false;
  isRestoring = false;
  isUploadModalOpen = false;
  isUploadingAttachment = false;
  uploadFeatureId: number | null = null;
  uploadFiles: File[] = [];
  uploadExistingAttachments: RenterAttachment[] = [];
  deletingAttachmentIds: number[] = [];
  uploadMode: 'create' | 'edit' = 'create';
  uploadError: string | null = null;
  pendingDeleteOnlyUpload = false;
  isCropModalOpen = false;
  isApplyingCrop = false;
  cropSourceUrl: string | null = null;
  cropSourceName = '';
  cropSourceFile: File | null = null;
  cropError: string | null = null;
  cropZoom = 1;
  cropOffsetX = 0;
  cropOffsetY = 0;
  cropImageNaturalWidth = 0;
  cropImageNaturalHeight = 0;
  imagePreviewUrl: string | null = null;
  imagePreviewName: string | null = null;
  isImagePreviewOpen = false;
  renterProfileImageUrl: string | null = null;
  isRenterInfoOpen = false;
  isRenterUsernameVisible = false;
  renterUsernameCopied = false;
  reportBrandName = 'HTALLY';
  reportBrandLogoUrl: string | null = null;
  readonly renterFeatureType = FeatureTypeEnum.RENTER_IMAGE;
  readonly renterMaxFileCount = ATTACHMENT_MAX_FILE_COUNT_BY_FEATURE[FeatureTypeEnum.RENTER_IMAGE];
  readonly renterAllowedExtensionsText = ATTACHMENT_ALLOWED_EXTENSIONS_BY_FEATURE[FeatureTypeEnum.RENTER_IMAGE].join(', ');
  private cropImageElement: HTMLImageElement | null = null;
  private activeCropPointerId: number | null = null;
  private cropDragOriginX = 0;
  private cropDragOriginY = 0;
  private cropDragStartOffsetX = 0;
  private cropDragStartOffsetY = 0;

  tableHeaderAndValueKeySets: TableHeaderAndValueKeySets = {
    colorKey: 'status',
    'table.name': {
      topLineKey: 'nameTopLine',
      bottomLineKyes: ['nameBottomLine']
    },
    'table.mobile': 'renterMobile',
    'table.photoId': 'renterPhotoIdNo'
  };
  readonly renterMenuActions: EntityListMenuAction[] = [
    { key: 'edit', label: 'action.edit' },
    { key: 'delete', label: 'action.delete', danger: true, requiredService: 'RENTER_DELETE' }
  ];
  readonly deletedRenterMenuActions: EntityListMenuAction[] = [
    { key: 'restore', label: 'Restore', requiredService: 'RENTER_DELETE' }
  ];

  get deletedMode(): boolean {
    return Number(this.filters?.['status']) === 0;
  }

  get visibleRenterMenuActions(): EntityListMenuAction[] {
    return this.deletedMode ? this.deletedRenterMenuActions : this.renterMenuActions;
  }

  get isCropDragging(): boolean {
    return this.activeCropPointerId !== null;
  }

  constructor(
    private readonly renters: RenterService,
    private readonly tokens: TokenStorageService,
    private readonly appUsers: AppUserService,
    private readonly attachments: AttachmentService,
    private readonly mappings: FlatRenterMappingService,
    private readonly payments: PaymentService,
    private readonly language: LanguageService,
    private readonly subscriptionService: SubscriptionService,
    private readonly snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.isAuthenticated = this.tokens.hasActiveSession;
    if (this.isAuthenticated) {
      this.status = 'Loading renter list...';
      this.loadReportBranding();
      this.loadSubscriptionSummary();
      this.summaryRefreshSubscription = this.subscriptionService.summaryRefresh$.subscribe(() => {
        this.loadSubscriptionSummary();
      });
      this.fetchRenters(true);
    } else {
      this.error = 'Please log in first.';
    }
  }

  ngOnDestroy(): void {
    this.summaryRefreshSubscription?.unsubscribe();
  }

  get hasMoreRenters(): boolean {
    return hasMoreByTotal(this.renterList, this.paginationState);
  }

  get renterCreateLimit(): number {
    return Number(this.subscriptionSummary?.currentPackage?.maxActiveRenter ?? 0);
  }

  get activeRenterCount(): number {
    return Number(this.subscriptionSummary?.usage?.activeRenterCount ?? 0);
  }

  get isCreateDisabled(): boolean {
    return this.renterCreateLimit > 0 && this.activeRenterCount >= this.renterCreateLimit;
  }

  showCreateLimitMessage(): string {
    return `Renter create limit is ${this.renterCreateLimit}.`;
  }

  fetchRenters(reset = true): void {
    if ((reset && this.isLoading) || (!reset && (this.isLoading || this.isLoadingMore || !this.hasMoreRenters))) {
      return;
    }

    if (reset) {
      this.isLoading = true;
      this.error = null;
      this.renterList = [];
      resetListPaginationState(this.paginationState);
    } else {
      this.isLoadingMore = true;
    }

    const filters = { ...this.filters };
    if (!Object.prototype.hasOwnProperty.call(filters, 'status')) {
      filters['status'] = 1;
    }
    const requestPage = resolveRequestPage(this.paginationState, reset);
    this.resolveRenterRequestFilters(filters)
      .pipe(
        switchMap((resolvedFilters: Record<string, any>) => this.renters.list({
          pagination: this.paginationState.pagination,
          pageNo: requestPage,
          rowCount: this.paginationState.rowCount,
          filters: resolvedFilters
        }) as Observable<ApiResponse>),
        finalize(() => {
          if (reset) {
            this.isLoading = false;
          } else {
            this.isLoadingMore = false;
          }
        })
      )
      .subscribe({
        next: (res: ApiResponse) => {
          if (res && res.code === '000') {
            const nextRows = (res.data || []).map((renter) => ({
              ...renter,
              nameTopLine: renter.renterName || 'N/A',
              nameBottomLine: renter.renterEmail || 'N/A',
              statusLabelText: renter.status === 1 ? 'Active' : 'Inactive'
            }));
            updateAfterFetch(this.paginationState, res?.totalRows, requestPage, nextRows.length);
            this.renterList = reset ? nextRows : appendUniqueById(this.renterList, nextRows);

            const selectedRenterId = Number(this.selectedRenter?.id);
            if (Number.isFinite(selectedRenterId)) {
              const latestSelectedRenter = this.renterList.find((item) => Number(item.id) === selectedRenterId);
              if (latestSelectedRenter) {
                this.selectedRenter = latestSelectedRenter;
              }
            }

            this.status = `Total renters: ${this.paginationState.totalRows || this.renterList.length}`;
          } else {
            this.error = res?.message || 'Unknown error occurred.';
          }
        },
        error: (err: any) => {
          this.error = err?.message || 'Error fetching renters';
        }
      });
  }

  setFilters(filters: Record<string, any>): void {
    this.filters = { ...filters };
    this.fetchRenters(true);
  }

  onLoadMoreFromList(): void {
    this.fetchRenters(false);
  }

  onCardClickFromList(item: Record<string, any>): void {
    this.selectedRenter = item as Renter;
    this.isDetailsOpen = true;
    this.loadRenterProfileImage();
    this.loadRenterDetailsData();
  }

  onEditClickFromList(item: Record<string, any>): void {
    this.selectedRenter = item as Renter;
    this.isEditOpen = true;
  }

  onDeleteClickFromList(item: Record<string, any>): void {
    if (this.isDeleting) {
      return;
    }
    this.pendingDeleteRenter = item as Renter;
  }

  onMenuActionFromList(event: { actionKey: string; item: Record<string, any> }): void {
    if (event?.actionKey === 'restore') {
      this.onRestoreClickFromList(event.item);
    }
  }

  onRestoreClickFromList(item: Record<string, any>): void {
    if (this.isRestoring) {
      return;
    }
    this.pendingRestoreRenter = item as Renter;
  }

  closeDeleteModal(): void {
    if (this.isDeleting) {
      return;
    }
    this.pendingDeleteRenter = null;
  }

  closeRestoreModal(): void {
    if (this.isRestoring) {
      return;
    }
    this.pendingRestoreRenter = null;
  }

  confirmDeleteRenter(): void {
    const renter = this.pendingDeleteRenter;
    if (!renter || this.isDeleting) {
      return;
    }

    this.isDeleting = true;
    this.error = null;
    this.renters.delete({ id: renter.id })
      .pipe(finalize(() => (this.isDeleting = false)))
      .subscribe({
        next: () => {
          this.pendingDeleteRenter = null;
          this.fetchRenters(true);
        },
        error: (err: any) => {
          this.pendingDeleteRenter = null;
          this.error = err?.message || 'Failed to delete renter.';
        }
      });
  }

  confirmRestoreRenter(): void {
    const renter = this.pendingRestoreRenter;
    if (!renter || this.isRestoring) {
      return;
    }

    this.isRestoring = true;
    this.error = null;
    this.renters.restore({ id: renter.id })
      .pipe(finalize(() => (this.isRestoring = false)))
      .subscribe({
        next: () => {
          this.pendingRestoreRenter = null;
          this.fetchRenters(true);
        },
        error: (err: any) => {
          this.pendingRestoreRenter = null;
          this.error = err?.message || 'Failed to restore renter.';
        }
      });
  }

  closeDetailsModal(): void {
    this.isDetailsOpen = false;
    this.isRenterInfoOpen = false;
    this.isRenterUsernameVisible = false;
    this.renterUsernameCopied = false;
    this.detailsDataError = null;
    this.isDetailsDataLoading = false;
    this.renterMappings = [];
    this.renterFlatOptions = [];
    this.selectedFlatId = null;
    this.selectedPaymentYear = `${new Date().getFullYear()}`;
    this.renterProfileImageUrl = null;
    this.selectedRenterMapping = null;
    this.renterPaymentItems = [];
    this.renterPaymentRows = [];
  }

  openRenterInfoModal(): void {
    this.isRenterInfoOpen = true;
    this.isRenterUsernameVisible = false;
    this.renterUsernameCopied = false;
  }

  closeRenterInfoModal(): void {
    this.isRenterInfoOpen = false;
    this.isRenterUsernameVisible = false;
    this.renterUsernameCopied = false;
  }

  closeEditModal(): void {
    this.isEditOpen = false;
    this.selectedRenter = null;
  }

  onEditSave(event?: RenterSaveEvent): void {
    const previousSelectedRenter = this.selectedRenter;
    this.isEditOpen = false;
    this.selectedRenter = null;
    this.fetchRenters(true);

    if (event?.mode === 'create' && Number.isFinite(Number(event?.refId))) {
      this.openUploadAfterCreate(Number(event.refId), previousSelectedRenter);
    }
  }

  openUploadAfterCreate(refId: number, renter: Renter | null = null): void {
    if (!Number.isFinite(Number(refId))) {
      return;
    }

    this.clearCropState();
    this.uploadFeatureId = Number(refId);
    this.uploadMode = 'create';
    this.uploadFiles = [];
    this.deletingAttachmentIds = [];
    this.uploadExistingAttachments = Array.isArray(renter?.attachments)
      ? [...(renter?.attachments as RenterAttachment[])]
      : [];
    this.uploadError = null;
    this.isUploadModalOpen = true;
  }

  openEditDocumentsModal(): void {
    if (!this.selectedRenter || !Number.isFinite(Number(this.selectedRenter.id))) {
      return;
    }

    this.clearCropState();
    this.uploadFeatureId = Number(this.selectedRenter.id);
    this.uploadMode = 'edit';
    this.uploadFiles = [];
    this.deletingAttachmentIds = [];
    this.uploadExistingAttachments = Array.isArray(this.selectedRenter.attachments)
      ? [...(this.selectedRenter.attachments as RenterAttachment[])]
      : [];
    this.uploadError = null;
    this.isUploadModalOpen = true;
  }

  formatDateTime(value: string | null | undefined): string {
    if (!value) {
      return 'N/A';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return 'N/A';
    }

    const day = `${date.getDate()}`.padStart(2, '0');
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const year = date.getFullYear();
    const rawHours = date.getHours();
    const minutes = `${date.getMinutes()}`.padStart(2, '0');
    const period = rawHours >= 12 ? 'pm' : 'am';
    const hours12 = rawHours % 12 || 12;
    const hours = `${hours12}`.padStart(2, '0');

    return `${day}-${month}-${year} ${hours}:${minutes} ${period}`;
  }

  formatDate(value: string | null | undefined): string {
    if (!value) {
      return 'N/A';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return `${value}`;
    }

    const day = `${date.getDate()}`.padStart(2, '0');
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  formatAmount(value: number | null | undefined): string {
    const amount = Number(value ?? 0);
    return Number.isFinite(amount) ? amount.toLocaleString('en-US') : '0';
  }

  formatDeductionAmount(value: number | null | undefined): string {
    const amount = Number(value ?? 0);
    if (!Number.isFinite(amount) || amount <= 0) {
      return this.formatAmount(0);
    }
    return `(${this.formatAmount(amount)})`;
  }

  mappingStatusLabel(value: number | null | undefined): string {
    return Number(value) === 1
      ? this.t('common.active', 'Active')
      : this.t('common.inactive', 'Inactive');
  }

  mappingFlatLabel(item: RenterMappingInfo | null | undefined): string {
    const flatName = `${item?.flat?.flatName ?? ''}`.trim();
    const floorName = `${item?.flat?.floorName ?? ''}`.trim();
    const houseName = `${item?.flat?.house?.houseName ?? ''}`.trim();
    const houseAddress = `${item?.flat?.house?.houseAddress ?? ''}`.trim();
    const floorLabel = floorName ? `${this.t('report.floor', 'Floor')} ${floorName}` : '';
    const chunks = [flatName, floorLabel, houseName, houseAddress].filter((x) => !!x);
    return chunks.length > 0 ? chunks.join(', ') : this.t('common.na', 'N/A');
  }

  mappingRemainingAdvance(item: RenterMappingInfo | null | undefined): number {
    const advance = Number(item?.advanceAmount ?? 0);
    const used = Number(item?.usedAdvanceAmount ?? 0);
    return Math.max(0, advance - used);
  }

  onDetailsFlatChange(value: string): void {
    const flatId = Number(value);
    this.selectedFlatId = Number.isFinite(flatId) ? flatId : null;
    this.syncSelectedMappingByFlat();
    this.fetchRenterPayments();
  }

  onDetailsFlatDropdownChange(event: { value: unknown }): void {
    this.onDetailsFlatChange(`${event?.value ?? ''}`);
  }

  onDetailsYearChange(value: string): void {
    const cleaned = `${value ?? ''}`.replace(/[^0-9]/g, '').slice(0, 4);
    this.selectedPaymentYear = cleaned;
  }

  onDetailsYearDropdownChange(event: { value: unknown }): void {
    this.onDetailsYearChange(`${event?.value ?? ''}`);
  }

  renterDetailsFlatDropdownOptions(): Record<string, unknown>[] {
    return this.renterFlatOptions.map((item) => ({
      value: item.value,
      label: item.label
    }));
  }

  applyDetailsFilters(): void {
    if (!this.selectedPaymentYear || this.selectedPaymentYear.length !== 4) {
      this.detailsDataError = this.t('renter.invalidYear', 'Please provide a valid year (YYYY).');
      return;
    }
    this.fetchRenterPayments();
  }

  isImageFile(fileName: string | null | undefined): boolean {
    const name = `${fileName ?? ''}`.toLowerCase().trim();
    return name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png') || name.endsWith('.webp');
  }

  firstRenterImageAttachment(): RenterAttachment | null {
    const attachments = Array.isArray(this.selectedRenter?.attachments) ? this.selectedRenter.attachments : [];
    const match = attachments.find((item) => this.isImageFile(item?.attachmentFileName));
    return match || null;
  }

  renterInitials(): string {
    const name = `${this.selectedRenter?.renterName ?? ''}`.trim();
    if (!name) {
      return 'R';
    }
    const parts = name.split(/\s+/).filter((p) => !!p);
    if (parts.length === 1) {
      return parts[0].slice(0, 1).toUpperCase();
    }
    return `${parts[0].slice(0, 1)}${parts[1].slice(0, 1)}`.toUpperCase();
  }

  renterUsernameValue(): string {
    const directUsername = `${this.selectedRenter?.renterUsername ?? ''}`.trim();
    if (directUsername) {
      return directUsername;
    }

    const nestedUsername = `${(this.selectedRenter as any)?.renterUser?.username ?? ''}`.trim();
    return nestedUsername || 'N/A';
  }

  maskedRenterUsername(): string {
    const username = this.renterUsernameValue();
    if (!username || username === 'N/A') {
      return 'N/A';
    }
    return '•'.repeat(Math.max(8, username.length));
  }

  toggleRenterUsernameVisibility(): void {
    if (this.renterUsernameValue() === 'N/A') {
      return;
    }
    this.isRenterUsernameVisible = !this.isRenterUsernameVisible;
  }

  async copyRenterUsername(): Promise<void> {
    const username = this.renterUsernameValue();
    if (username === 'N/A') {
      return;
    }

    const copied = await this.copyText(username);
    this.renterUsernameCopied = copied;
    if (copied) {
      window.setTimeout(() => {
        this.renterUsernameCopied = false;
      }, 1500);
    }
  }

  selectedAgreementAttachments(): RenterAttachment[] {
    const flatId = Number(this.selectedFlatId);
    const rows = Number.isFinite(flatId)
      ? this.renterMappings.filter((item) => Number(item?.flat?.id) === flatId)
      : (this.selectedRenterMapping ? [this.selectedRenterMapping] : []);

    const merged = rows.flatMap((item) => (Array.isArray(item?.attachments) ? item.attachments : []));
    const seen = new Set<number>();
    const unique: RenterAttachment[] = [];
    merged.forEach((attachment) => {
      const id = Number(attachment?.id);
      if (!Number.isFinite(id) || seen.has(id)) {
        return;
      }
      seen.add(id);
      unique.push(attachment);
    });
    return unique;
  }

  onUploadFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const incoming = Array.from(input?.files ?? []);
    const maxCount = ATTACHMENT_MAX_FILE_COUNT_BY_FEATURE[this.renterFeatureType];
    const allowedExtensions = ATTACHMENT_ALLOWED_EXTENSIONS_BY_FEATURE[this.renterFeatureType];

    if (maxCount === 1) {
      if (this.uploadExistingAttachments.length >= 1) {
        this.uploadError = 'Remove the existing renter image before selecting a new one.';
        if (input) {
          input.value = '';
        }
        return;
      }

      const selected = incoming[0];
      if (!selected) {
        this.uploadFiles = [];
        this.uploadError = null;
        if (input) {
          input.value = '';
        }
        return;
      }

      const selectedName = `${selected.name ?? ''}`.toLowerCase();
      if (!allowedExtensions.some((ext) => selectedName.endsWith(ext))) {
        this.uploadError = `Only ${this.renterAllowedExtensionsText} files are allowed.`;
        if (input) {
          input.value = '';
        }
        return;
      }

      this.uploadError = incoming.length > 1 ? 'Only 1 file allowed for renter image.' : null;
      this.openCropModalForFile(selected);

      if (input) {
        input.value = '';
      }
      return;
    }

    const current = [...this.uploadFiles];
    const merged = [...current];
    const existingNames = new Set(
      this.uploadExistingAttachments
        .map((item) => `${item?.attachmentFileName ?? ''}`.trim().toLowerCase())
        .filter((name) => !!name)
    );
    const duplicateNames: string[] = [];

    incoming.forEach((file) => {
      const normalizedName = `${file?.name ?? ''}`.trim().toLowerCase();
      const existsInSelected = merged.some(
        (item) => `${item?.name ?? ''}`.trim().toLowerCase() === normalizedName
      );
      const exists = existsInSelected || existingNames.has(normalizedName);
      if (!exists) {
        merged.push(file);
      } else if (normalizedName) {
        duplicateNames.push(file.name);
      }
    });
    this.uploadFiles = merged;

    if (duplicateNames.length > 0) {
      this.uploadError = `Duplicate file name not allowed: ${duplicateNames.join(', ')}`;
    } else {
      this.uploadError = null;
    }

    if (input) {
      input.value = '';
    }
  }

  uploadRenterImage(): void {
    if (!this.uploadFeatureId) {
      this.uploadError = 'Invalid renter id for attachment upload.';
      return;
    }

    const willDeleteOnly = this.uploadMode === 'edit' && this.deletingAttachmentIds.length > 0 && this.uploadFiles.length === 0;
    if (willDeleteOnly) {
      this.pendingDeleteOnlyUpload = true;
      return;
    }
    this.performRenterImageUpload();
  }

  confirmDeleteOnlyUpload(): void {
    if (!this.pendingDeleteOnlyUpload) {
      return;
    }
    this.pendingDeleteOnlyUpload = false;
    this.performRenterImageUpload();
  }

  closeDeleteOnlyUploadModal(): void {
    this.pendingDeleteOnlyUpload = false;
  }

  private performRenterImageUpload(): void {
    const featureId = this.uploadFeatureId;
    if (!featureId) {
      this.uploadError = 'Invalid renter id for attachment upload.';
      return;
    }

    const willDeleteOnly = this.uploadMode === 'edit' && this.deletingAttachmentIds.length > 0 && this.uploadFiles.length === 0;
    if (!this.uploadFiles.length && !willDeleteOnly) {
      this.uploadError = 'Please select at least one file.';
      return;
    }

    const featureType = this.renterFeatureType;
    const maxCount = ATTACHMENT_MAX_FILE_COUNT_BY_FEATURE[featureType];
    const allowedExtensions = ATTACHMENT_ALLOWED_EXTENSIONS_BY_FEATURE[featureType];

    const finalCount = this.uploadExistingAttachments.length + this.uploadFiles.length;
    if (finalCount > maxCount) {
      this.uploadError = `Maximum ${maxCount} file allowed for renter image.`;
      return;
    }

    const invalid = this.uploadFiles.find((file) => {
      const name = `${file.name ?? ''}`.toLowerCase();
      return !allowedExtensions.some((ext) => name.endsWith(ext));
    });

    if (invalid) {
      this.uploadError = `Only ${this.renterAllowedExtensionsText} files are allowed.`;
      return;
    }

    const oversize = this.uploadFiles.find((file) => Number(file?.size ?? 0) > ATTACHMENT_MAX_FILE_SIZE_BYTES);
    if (oversize) {
      this.uploadError = 'Each file must be 2MB or smaller.';
      return;
    }

    this.isUploadingAttachment = true;
    this.uploadError = null;

    this.attachments.manageAttachment({
      featureType,
      featureId,
      actionType: this.uploadMode === 'edit' ? 2 : 1,
      files: this.uploadFiles,
      deletingIds: this.deletingAttachmentIds,
      service: 'RENTER_IMAGE_CREATE'
    })
      .pipe(finalize(() => (this.isUploadingAttachment = false)))
      .subscribe({
        next: () => {
          if (this.uploadMode === 'edit') {
            this.snackBar.open(this.t('renter.imageUpdatedSuccess', 'Renter image updated successfully.'), this.t('common.close', 'Close'), {
              duration: 3000,
              horizontalPosition: 'right',
              verticalPosition: 'top'
            });
          }
          this.closeUploadModal();
          this.fetchRenters(true);
        },
        error: (err: any) => {
          this.uploadError = err?.message || 'Failed to upload attachment.';
        }
      });
  }

  closeUploadModal(): void {
    const shouldEmitPostCreateFinished = this.uploadMode === 'create' && Number.isFinite(Number(this.uploadFeatureId));
    this.clearCropState();
    this.isUploadModalOpen = false;
    this.uploadFeatureId = null;
    this.uploadFiles = [];
    this.uploadExistingAttachments = [];
    this.deletingAttachmentIds = [];
    this.uploadMode = 'create';
    this.uploadError = null;
    this.pendingDeleteOnlyUpload = false;
    if (shouldEmitPostCreateFinished) {
      this.postCreateUploadFinished.emit();
    }
  }

  skipUploadModal(): void {
    this.closeUploadModal();
  }

  removeUploadFile(index: number): void {
    if (index < 0 || index >= this.uploadFiles.length) {
      return;
    }
    this.uploadFiles = this.uploadFiles.filter((_, i) => i !== index);
  }

  formatFileSize(value: number | null | undefined): string {
    const bytes = Number(value ?? 0);
    if (!Number.isFinite(bytes) || bytes <= 0) {
      return '0 KB';
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(bytes >= 100 * 1024 ? 0 : 1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
  }

  onCropImageLoad(event: Event): void {
    const image = event.target as HTMLImageElement | null;
    if (!image) {
      this.cropError = 'Failed to load the selected image.';
      return;
    }

    this.cropImageElement = image;
    this.cropImageNaturalWidth = Number(image.naturalWidth || image.width || 0);
    this.cropImageNaturalHeight = Number(image.naturalHeight || image.height || 0);
    this.cropError = null;
    this.resetCropTransform();
    this.queueCropClamp();
  }

  onCropZoomChange(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const nextZoom = Number(input?.value ?? this.cropZoom);
    this.cropZoom = this.clampNumber(nextZoom, 1, 4);
    this.clampCropOffsets();
  }

  onCropWheel(event: WheelEvent): void {
    if (!this.cropImageNaturalWidth || !this.cropImageNaturalHeight) {
      return;
    }

    event.preventDefault();
    const direction = event.deltaY < 0 ? 0.08 : -0.08;
    this.cropZoom = this.clampNumber(this.cropZoom + direction, 1, 4);
    this.clampCropOffsets();
  }

  startCropDrag(event: PointerEvent): void {
    if (!this.cropImageNaturalWidth || !this.cropImageNaturalHeight) {
      return;
    }

    this.activeCropPointerId = event.pointerId;
    this.cropDragOriginX = event.clientX;
    this.cropDragOriginY = event.clientY;
    this.cropDragStartOffsetX = this.cropOffsetX;
    this.cropDragStartOffsetY = this.cropOffsetY;

    const target = event.currentTarget as HTMLElement | null;
    target?.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  }

  onCropDrag(event: PointerEvent): void {
    if (this.activeCropPointerId !== event.pointerId) {
      return;
    }

    this.cropOffsetX = this.cropDragStartOffsetX + (event.clientX - this.cropDragOriginX);
    this.cropOffsetY = this.cropDragStartOffsetY + (event.clientY - this.cropDragOriginY);
    this.clampCropOffsets();
  }

  endCropDrag(event: PointerEvent): void {
    if (this.activeCropPointerId !== event.pointerId) {
      return;
    }

    const target = event.currentTarget as HTMLElement | null;
    target?.releasePointerCapture?.(event.pointerId);
    this.activeCropPointerId = null;
  }

  resetCropTransform(): void {
    this.cropZoom = 1;
    this.cropOffsetX = 0;
    this.cropOffsetY = 0;
    this.clampCropOffsets();
  }

  cropImageTransform(): string {
    const scale = this.cropRenderScale();
    return `translate(-50%, -50%) translate(${this.cropOffsetX}px, ${this.cropOffsetY}px) scale(${scale})`;
  }

  closeCropModal(): void {
    this.clearCropState();
  }

  async applyCropSelection(): Promise<void> {
    if (!this.cropSourceFile || !this.cropImageElement) {
      this.cropError = 'Select an image first.';
      return;
    }

    this.isApplyingCrop = true;
    this.cropError = null;

    try {
      const croppedFile = await this.buildCroppedUploadFile();
      if (Number(croppedFile.size ?? 0) > ATTACHMENT_MAX_FILE_SIZE_BYTES) {
        this.cropError = 'Cropped image is still larger than 2MB. Please zoom in a bit more or choose a smaller image.';
        return;
      }

      this.uploadFiles = [croppedFile];
      this.uploadError = null;
      this.clearCropState();
    } catch (error: any) {
      this.cropError = error?.message || 'Failed to crop image.';
    } finally {
      this.isApplyingCrop = false;
    }
  }

  removeExistingAttachment(index: number): void {
    const target = this.uploadExistingAttachments[index];
    if (!target) {
      return;
    }

    const id = Number(target.id);
    if (Number.isFinite(id) && !this.deletingAttachmentIds.includes(id)) {
      this.deletingAttachmentIds.push(id);
    }
    this.uploadExistingAttachments = this.uploadExistingAttachments.filter((_, i) => i !== index);
  }

  openAttachmentPreview(attachment: RenterAttachment): void {
    const attachmentId = Number(attachment?.id);
    if (!Number.isFinite(attachmentId)) {
      const fallbackUrl = `${attachment?.attachmentUrl ?? ''}`.trim();
      if (fallbackUrl) {
        this.openPreviewByType(fallbackUrl, `${attachment?.attachmentFileName ?? ''}`);
      }
      return;
    }

    this.attachments.previewUrlByAttachmentId(attachmentId).subscribe({
      next: (url: string) => this.openPreviewByType(url, `${attachment?.attachmentFileName ?? ''}`),
      error: (err: any) => {
        this.uploadError = err?.message || 'Failed to open attachment preview.';
      }
    });
  }

  closeImagePreview(): void {
    this.isImagePreviewOpen = false;
    this.imagePreviewUrl = null;
    this.imagePreviewName = null;
  }

  async downloadPreviewImage(): Promise<void> {
    const url = `${this.imagePreviewUrl ?? ''}`.trim();
    if (!url) {
      return;
    }

    const fileName = this.imagePreviewName || 'image';
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to download (${response.status})`);
      }
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = fileName;
      anchor.click();
      window.URL.revokeObjectURL(objectUrl);
    } catch {
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      anchor.click();
    }
  }

  private openPreviewByType(url: string, fileNameRaw: string): void {
    const fileName = `${fileNameRaw ?? ''}`.toLowerCase();
    if (fileName.endsWith('.pdf')) {
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      anchor.click();
      return;
    }

    this.imagePreviewUrl = url;
    this.imagePreviewName = fileNameRaw || 'Attachment';
    this.isImagePreviewOpen = true;
  }

  private loadRenterProfileImage(): void {
    this.renterProfileImageUrl = null;
    const attachment = this.firstRenterImageAttachment();
    const attachmentId = Number(attachment?.id);
    if (!Number.isFinite(attachmentId)) {
      return;
    }

    this.attachments.previewUrlByAttachmentId(attachmentId).subscribe({
      next: (url: string) => {
        this.renterProfileImageUrl = `${url ?? ''}`.trim() || null;
      },
      error: () => {
        this.renterProfileImageUrl = null;
      }
    });
  }

  private loadRenterDetailsData(): void {
    const renterId = Number(this.selectedRenter?.id);
    if (!Number.isFinite(renterId)) {
      this.renterMappings = [];
      this.renterFlatOptions = [];
      this.selectedFlatId = null;
      this.selectedRenterMapping = null;
      this.renterPaymentItems = [];
      this.renterPaymentRows = [];
      return;
    }

    this.isDetailsDataLoading = true;
    this.detailsDataError = null;

    this.mappings.list({
      pagination: true,
      pageNo: 0,
      rowCount: 20,
      filters: {
        'renter.id': renterId
      }
    })
      .pipe(
        catchError(() => of({ data: [] })),
        finalize(() => (this.isDetailsDataLoading = false))
      )
      .subscribe({
        next: (mappingRes: any) => {
          const mappings = Array.isArray(mappingRes?.data) ? (mappingRes.data as RenterMappingInfo[]) : [];
          this.renterMappings = mappings;
          this.renterFlatOptions = this.buildFlatOptions(mappings);
          this.selectedFlatId = this.renterFlatOptions.length > 0 ? this.renterFlatOptions[0].value : null;
          this.syncSelectedMappingByFlat();
          this.fetchRenterPayments();
        },
        error: (err: any) => {
          this.detailsDataError = err?.message || this.t('renter.failedToLoadDetails', 'Failed to load renter details.');
          this.renterMappings = [];
          this.renterFlatOptions = [];
          this.selectedFlatId = null;
          this.selectedRenterMapping = null;
          this.renterPaymentItems = [];
          this.renterPaymentRows = [];
        }
      });
  }

  private fetchRenterPayments(): void {
    const renterId = Number(this.selectedRenter?.id);
    if (!Number.isFinite(renterId)) {
      this.renterPaymentItems = [];
      this.renterPaymentRows = [];
      return;
    }

    const year = `${this.selectedPaymentYear ?? ''}`.trim();
    if (!year || year.length !== 4) {
      this.renterPaymentItems = [];
      this.renterPaymentRows = [];
      return;
    }

    this.isDetailsDataLoading = true;
    this.detailsDataError = null;

    const filters: Record<string, any> = {
      'flatRenterMapping.renter.id': renterId,
      paymentYear: year,
      status: 1
    };
    if (Number.isFinite(Number(this.selectedFlatId))) {
      filters['flatRenterMapping.flat.id'] = Number(this.selectedFlatId);
    }

    this.payments.list({
      pagination: true,
      pageNo: 0,
      rowCount: 50,
      withTotals: true,
      filters
    })
      .pipe(finalize(() => (this.isDetailsDataLoading = false)))
      .subscribe({
        next: (res: any) => {
          const payments = Array.isArray(res?.data) ? (res.data as RenterPaymentItem[]) : [];
          this.renterPaymentItems = payments;
          this.renterPaymentRows = payments.map((payment) => ({
            monthLabel: this.paymentMonthLabel(payment.paymentMonth, payment.paymentYear),
            paymentDateLabel: this.formatDate(payment.paymentDate),
            rentLabel: this.formatAmount(payment.rentAmount),
            gasLabel: this.formatAmount(payment.gasBillAmount),
            electricLabel: this.formatAmount(payment.electricBillAmount),
            waterLabel: this.formatAmount(payment.waterBillAmount),
            serviceLabel: this.formatAmount(payment.serviceChargeAndOtherAmount),
            otherLabel: this.formatDeductionAmount(payment.negativeChargeAmount),
            advanceAdjustedLabel: this.formatDeductionAmount(payment.advanceAdjustedAmount),
            totalLabel: this.formatAmount(this.paymentTotal(payment))
          }));
        },
        error: (err: any) => {
          this.detailsDataError = err?.message || this.t('renter.failedToLoadPaymentList', 'Failed to load renter payment list.');
          this.renterPaymentItems = [];
          this.renterPaymentRows = [];
        }
      });
  }

  private buildFlatOptions(items: RenterMappingInfo[]): RenterFlatOption[] {
    const seen = new Set<number>();
    const options: RenterFlatOption[] = [];
    items.forEach((item) => {
      const flatId = Number(item?.flat?.id);
      if (!Number.isFinite(flatId) || seen.has(flatId)) {
        return;
      }
      seen.add(flatId);
      const flatName = `${item?.flat?.flatName ?? 'N/A'}`.trim();
      const floor = `${item?.flat?.floorName ?? ''}`.trim();
      const house = `${item?.flat?.house?.houseName ?? ''}`.trim();
      const labelParts = [
        flatName,
        floor ? `${this.t('report.floor', 'Floor')} ${floor}` : '',
        house
      ].filter((x) => !!x);
      options.push({
        value: flatId,
        label: labelParts.join(', ')
      });
    });
    return options;
  }

  private syncSelectedMappingByFlat(): void {
    const flatId = Number(this.selectedFlatId);
    if (!Number.isFinite(flatId)) {
      this.selectedRenterMapping = this.pickBestMapping(this.renterMappings);
      return;
    }
    const matched = this.renterMappings.filter((item) => Number(item?.flat?.id) === flatId);
    this.selectedRenterMapping = this.pickBestMapping(matched);
  }

  private pickBestMapping(items: RenterMappingInfo[]): RenterMappingInfo | null {
    if (!Array.isArray(items) || items.length === 0) {
      return null;
    }

    const sorted = [...items].sort((a, b) => {
      const aTime = new Date(a?.updatedOn || a?.createdOn || 0).getTime();
      const bTime = new Date(b?.updatedOn || b?.createdOn || 0).getTime();
      return bTime - aTime;
    });

    const active = sorted.find((item) => Number(item?.status) === 1);
    return active || sorted[0] || null;
  }

  private paymentTotal(item: RenterPaymentItem): number {
    return (
      Number(item?.rentAmount ?? 0) +
      Number(item?.gasBillAmount ?? 0) +
      Number(item?.electricBillAmount ?? 0) +
      Number(item?.waterBillAmount ?? 0) +
      Number(item?.serviceChargeAndOtherAmount ?? 0) -
      Number(item?.negativeChargeAmount ?? 0) -
      Number(item?.advanceAdjustedAmount ?? 0)
    );
  }

  private paymentMonthLabel(month: number | null | undefined, year: string | number | null | undefined): string {
    const monthIndex = Number(month) - 1;
    const y = `${year ?? ''}`.trim();
    const monthName = monthIndex >= 0 && monthIndex <= 11
      ? this.t(MONTH_TRANSLATION_KEYS[monthIndex], new Date(2000, monthIndex, 1).toLocaleString('en-US', { month: 'long' }))
      : this.t('dashboard.unknownMonth', 'Unknown');
    return y ? `${monthName}, ${y}` : monthName;
  }

  private paymentMonthLabelForReport(month: number | null | undefined, year: string | number | null | undefined): string {
    const monthIndex = Number(month) - 1;
    const y = `${year ?? ''}`.trim();
    const monthName = monthIndex >= 0 && monthIndex <= 11
      ? new Date(2000, monthIndex, 1).toLocaleString('en-US', { month: 'long' })
      : 'Unknown';
    return y ? `${monthName}, ${y}` : monthName;
  }

  private mappingStatusLabelForReport(value: number | null | undefined): string {
    return Number(value) === 1 ? 'Active' : 'Inactive';
  }

  private mappingFlatLabelForReport(item: RenterMappingInfo | null | undefined): string {
    const flatName = `${item?.flat?.flatName ?? ''}`.trim();
    const floorName = `${item?.flat?.floorName ?? ''}`.trim();
    const houseName = `${item?.flat?.house?.houseName ?? ''}`.trim();
    const houseAddress = `${item?.flat?.house?.houseAddress ?? ''}`.trim();
    const chunks = [flatName, floorName ? `Floor ${floorName}` : '', houseName, houseAddress].filter((x) => !!x);
    return chunks.length > 0 ? chunks.join(', ') : 'N/A';
  }

  private buildDetailsYearOptions(): Record<string, unknown>[] {
    const currentYear = new Date().getFullYear();
    const years: Record<string, unknown>[] = [];
    for (let offset = 0; offset <= 10; offset += 1) {
      const year = `${currentYear - offset}`;
      years.push({
        value: year,
        label: year
      });
    }
    return years;
  }

  async downloadDetailsPdfReport(): Promise<void> {
    await downloadRenterReportPdf(this.buildReportData());
  }

  downloadDetailsExcelReport(): void {
    downloadRenterReportExcel(this.buildReportData());
  }

  renterPaymentFinalTotalRow(): RenterPaymentRow {
    const totals = this.renterPaymentItems.reduce((acc, item) => {
      acc.rent += Number(item?.rentAmount ?? 0);
      acc.gas += Number(item?.gasBillAmount ?? 0);
      acc.electric += Number(item?.electricBillAmount ?? 0);
      acc.water += Number(item?.waterBillAmount ?? 0);
      acc.service += Number(item?.serviceChargeAndOtherAmount ?? 0);
      acc.other += Number(item?.negativeChargeAmount ?? 0);
      acc.advanceAdjusted += Number(item?.advanceAdjustedAmount ?? 0);
      acc.total += this.paymentTotal(item);
      return acc;
    }, {
      rent: 0,
      gas: 0,
      electric: 0,
      water: 0,
      service: 0,
      other: 0,
      advanceAdjusted: 0,
      total: 0
    });

    return {
      monthLabel: this.t('report.finalTotal', 'Final Total'),
      paymentDateLabel: '-',
      rentLabel: this.formatAmount(totals.rent),
      gasLabel: this.formatAmount(totals.gas),
      electricLabel: this.formatAmount(totals.electric),
      waterLabel: this.formatAmount(totals.water),
      serviceLabel: this.formatAmount(totals.service),
      otherLabel: this.formatDeductionAmount(totals.other),
      advanceAdjustedLabel: this.formatDeductionAmount(totals.advanceAdjusted),
      totalLabel: this.formatAmount(totals.total)
    };
  }

  private buildReportData(): RenterReportData {
    const generatedBy = this.resolveCurrentUserLabel();
    const flatName = `${this.selectedRenterMapping?.flat?.flatName ?? 'N/A'}`;
    const floorName = `${this.selectedRenterMapping?.flat?.floorName ?? 'N/A'}`;
    const houseName = `${this.selectedRenterMapping?.flat?.house?.houseName ?? 'N/A'}`;
    return {
      header: {
        renterName: `${this.selectedRenter?.renterName ?? 'N/A'}`,
        renterMobile: `${this.selectedRenter?.renterMobile ?? 'N/A'}`,
        renterEmail: `${this.selectedRenter?.renterEmail ?? 'N/A'}`,
        flat: this.mappingFlatLabelForReport(this.selectedRenterMapping),
        houseName,
        floorName,
        flatName,
        status: this.mappingStatusLabelForReport(this.selectedRenterMapping?.status),
        startDate: this.formatDate(this.selectedRenterMapping?.renterStartDate),
        endDate: this.formatDate(this.selectedRenterMapping?.renterEndDate),
        advanceAmount: this.formatAmount(this.selectedRenterMapping?.advanceAmount),
        advanceUsed: this.formatAmount(this.selectedRenterMapping?.usedAdvanceAmount),
        advanceRemain: this.formatAmount(this.mappingRemainingAdvance(this.selectedRenterMapping)),
        year: this.selectedPaymentYear || `${new Date().getFullYear()}`,
        negativeChargeTitle: `${this.selectedRenterMapping?.flat?.negativeChargeName ?? ''}`.trim() || 'Negative Charge'
      },
      rows: this.renterPaymentItems.map((item) => ({
        monthName: this.paymentMonthLabelForReport(item.paymentMonth, item.paymentYear),
        paymentDate: this.formatDate(item.paymentDate),
        homeRent: this.formatAmount(item.rentAmount),
        gasBill: this.formatAmount(item.gasBillAmount),
        electricBill: this.formatAmount(item.electricBillAmount),
        waterBill: this.formatAmount(item.waterBillAmount),
        serviceCharge: this.formatAmount(item.serviceChargeAndOtherAmount),
        otherCharges: this.formatDeductionAmount(item.negativeChargeAmount),
        advanceAdjusted: this.formatDeductionAmount(item.advanceAdjustedAmount),
        totalAmount: this.formatAmount(this.paymentTotal(item))
      })),
      brandName: this.reportBrandName,
      brandLogoUrl: this.reportBrandLogoUrl,
      generatedOn: this.formatDateTime(new Date().toISOString()),
      generatedBy,
      developedBy: APP_DEVELOPED_BY
    };
  }

  private resolveCurrentUserLabel(): string {
    const token = `${this.tokens.accessToken ?? ''}`.trim();
    if (!token) {
      return 'N/A';
    }

    const parts = token.split('.');
    if (parts.length < 2) {
      return 'N/A';
    }

    try {
      const base64 = parts[1]
        .replace(/-/g, '+')
        .replace(/_/g, '/')
        .padEnd(Math.ceil(parts[1].length / 4) * 4, '=');
      const jsonText = atob(base64);
      const payload = JSON.parse(jsonText) as Record<string, unknown>;
      const label = `${payload?.['name'] ?? payload?.['username'] ?? payload?.['preferred_username'] ?? payload?.['sub'] ?? ''}`.trim();
      return label || 'N/A';
    } catch {
      return 'N/A';
    }
  }

  private loadReportBranding(): void {
    this.appUsers.me().subscribe({
      next: (profile) => {
        if (!profile) {
          this.loadReportBrandingFromListFallback();
          return;
        }
        this.resolveReportBrandingProfile(profile);
      },
      error: () => this.loadReportBrandingFromListFallback()
    });
  }

  private loadReportBrandingFromListFallback(): void {
    this.appUsers.basicInfoFromList().subscribe({
      next: (profile) => {
        if (!profile) {
          this.reportBrandName = 'HTALLY';
          this.reportBrandLogoUrl = null;
          return;
        }
        this.resolveReportBrandingProfile(profile);
      },
      error: () => {
        this.reportBrandName = 'HTALLY';
        this.reportBrandLogoUrl = null;
      }
    });
  }

  private resolveReportBrandingProfile(profile: any): void {
    this.appUsers.resolveAccountOwnerProfile(profile).subscribe({
      next: (ownerProfile) => this.applyReportBranding(ownerProfile ?? profile),
      error: () => this.applyReportBranding(profile)
    });
  }

  private applyReportBranding(profile: any): void {
    const organizationName = `${profile?.organizationName ?? ''}`.trim();
    const userName = `${profile?.name ?? ''}`.trim();
    this.reportBrandName = organizationName || userName || 'HTALLY';

    const attachmentId = Number(profile?.attachments?.[0]?.id);
    if (!Number.isFinite(attachmentId)) {
      this.reportBrandLogoUrl = null;
      return;
    }

    this.attachments.previewUrlByAttachmentIdCached(attachmentId).subscribe({
      next: (url: string) => {
        this.reportBrandLogoUrl = `${url ?? ''}`.trim() || null;
      },
      error: () => {
        this.reportBrandLogoUrl = null;
      }
    });
  }

  private async copyText(value: string): Promise<boolean> {
    const text = `${value ?? ''}`.trim();
    if (!text) {
      return false;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      // Fallback below for browsers that block async clipboard access.
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', 'true');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();

    try {
      return document.execCommand('copy');
    } finally {
      document.body.removeChild(textarea);
    }
  }

  private t(key: string, fallback = ''): string {
    return this.language.translate(key, fallback);
  }

  private resolveRenterRequestFilters(filters: Record<string, any>): Observable<Record<string, any>> {
    const mappingFilters = this.extractMappingFilters(filters);
    if (Object.keys(mappingFilters).length === 0) {
      return of(filters);
    }

    const renterFilters = this.stripMappingFilters(filters);
    return this.mappings.list({
      pagination: false,
      pageNo: 0,
      rowCount: 5000,
      filters: mappingFilters
    }).pipe(
      map((res: { data?: RenterMappingInfo[] }) => {
        const renterIds = Array.from(
          new Set(
            (Array.isArray(res?.data) ? res.data : [])
              .map((item) => Number(item?.renter?.id))
              .filter((id) => Number.isFinite(id))
          )
        ) as number[];

        return {
          ...renterFilters,
          id: renterIds.length > 0 ? renterIds : [-1]
        };
      })
    );
  }

  private extractMappingFilters(filters: Record<string, any>): Record<string, any> {
    return this.mappingFilterKeys.reduce<Record<string, any>>((acc, key) => {
      const value = filters?.[key];
      if (value === null || value === undefined || `${value}`.trim() === '') {
        return acc;
      }
      acc[key] = value;
      return acc;
    }, {});
  }

  private stripMappingFilters(filters: Record<string, any>): Record<string, any> {
    const nextFilters = { ...filters };
    this.mappingFilterKeys.forEach((key) => delete nextFilters[key]);
    return nextFilters;
  }

  private loadSubscriptionSummary(): void {
    this.subscriptionService.getSubscriptionSummary().subscribe({
      next: (summary) => {
        this.subscriptionSummary = summary;
      },
      error: () => {
        this.subscriptionSummary = null;
      }
    });
  }

  private openCropModalForFile(file: File): void {
    this.clearCropState();
    this.cropSourceFile = file;
    this.cropSourceName = `${file?.name ?? ''}`.trim();
    this.cropSourceUrl = window.URL.createObjectURL(file);
    this.cropZoom = 1;
    this.cropOffsetX = 0;
    this.cropOffsetY = 0;
    this.cropError = null;
    this.isCropModalOpen = true;
    this.queueCropClamp();
  }

  private clearCropState(): void {
    if (this.cropSourceUrl) {
      window.URL.revokeObjectURL(this.cropSourceUrl);
    }

    this.isCropModalOpen = false;
    this.isApplyingCrop = false;
    this.cropSourceUrl = null;
    this.cropSourceName = '';
    this.cropSourceFile = null;
    this.cropError = null;
    this.cropZoom = 1;
    this.cropOffsetX = 0;
    this.cropOffsetY = 0;
    this.cropImageNaturalWidth = 0;
    this.cropImageNaturalHeight = 0;
    this.cropImageElement = null;
    this.activeCropPointerId = null;
    this.cropDragOriginX = 0;
    this.cropDragOriginY = 0;
    this.cropDragStartOffsetX = 0;
    this.cropDragStartOffsetY = 0;
  }

  private queueCropClamp(): void {
    window.requestAnimationFrame(() => this.clampCropOffsets());
  }

  private cropViewportDimension(): number {
    const element = this.cropViewportRef?.nativeElement;
    const width = Number(element?.clientWidth ?? 0);
    const height = Number(element?.clientHeight ?? 0);
    const dimension = Math.min(width, height);
    return dimension > 0 ? dimension : 320;
  }

  private cropRenderScale(): number {
    const width = Number(this.cropImageNaturalWidth ?? 0);
    const height = Number(this.cropImageNaturalHeight ?? 0);
    if (!width || !height) {
      return 1;
    }

    const viewport = this.cropViewportDimension();
    const baseScale = Math.max(viewport / width, viewport / height);
    return baseScale * this.cropZoom;
  }

  private clampCropOffsets(): void {
    const width = Number(this.cropImageNaturalWidth ?? 0);
    const height = Number(this.cropImageNaturalHeight ?? 0);
    if (!width || !height) {
      return;
    }

    const viewport = this.cropViewportDimension();
    const scale = this.cropRenderScale();
    const renderedWidth = width * scale;
    const renderedHeight = height * scale;
    const maxOffsetX = Math.max(0, (renderedWidth - viewport) / 2);
    const maxOffsetY = Math.max(0, (renderedHeight - viewport) / 2);

    this.cropOffsetX = this.clampNumber(this.cropOffsetX, -maxOffsetX, maxOffsetX);
    this.cropOffsetY = this.clampNumber(this.cropOffsetY, -maxOffsetY, maxOffsetY);
  }

  private async buildCroppedUploadFile(): Promise<File> {
    if (!this.cropSourceFile || !this.cropImageElement) {
      throw new Error('Select an image first.');
    }

    const viewport = this.cropViewportDimension();
    const scale = this.cropRenderScale();
    if (!viewport || !scale) {
      throw new Error('Crop area is not ready yet.');
    }

    const naturalWidth = Number(this.cropImageNaturalWidth ?? 0);
    const naturalHeight = Number(this.cropImageNaturalHeight ?? 0);
    if (!naturalWidth || !naturalHeight) {
      throw new Error('Failed to read image size.');
    }

    const srcSize = Math.min(viewport / scale, naturalWidth, naturalHeight);
    const centerX = naturalWidth / 2 - (this.cropOffsetX / scale);
    const centerY = naturalHeight / 2 - (this.cropOffsetY / scale);
    const srcX = this.clampNumber(centerX - (srcSize / 2), 0, Math.max(0, naturalWidth - srcSize));
    const srcY = this.clampNumber(centerY - (srcSize / 2), 0, Math.max(0, naturalHeight - srcSize));

    let outputSize = Math.min(720, Math.max(320, Math.round(srcSize)));
    let blob = await this.renderCroppedBlob(this.cropImageElement, srcX, srcY, srcSize, outputSize);

    while (blob.size > ATTACHMENT_MAX_FILE_SIZE_BYTES && outputSize > 320) {
      outputSize = Math.max(320, Math.round(outputSize * 0.85));
      blob = await this.renderCroppedBlob(this.cropImageElement, srcX, srcY, srcSize, outputSize);
    }

    return new File([blob], this.buildCroppedFileName(this.cropSourceFile.name), {
      type: 'image/jpeg',
      lastModified: Date.now()
    });
  }

  private async renderCroppedBlob(
    image: HTMLImageElement,
    srcX: number,
    srcY: number,
    srcSize: number,
    outputSize: number
  ): Promise<Blob> {
    const canvas = document.createElement('canvas');
    canvas.width = outputSize;
    canvas.height = outputSize;

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Canvas is not available in this browser.');
    }

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, outputSize, outputSize);
    context.drawImage(image, srcX, srcY, srcSize, srcSize, 0, 0, outputSize, outputSize);

    const qualities = [0.88, 0.8, 0.72, 0.64, 0.56];
    let latestBlob: Blob | null = null;

    for (const quality of qualities) {
      latestBlob = await this.canvasToBlob(canvas, quality);
      if (latestBlob.size <= ATTACHMENT_MAX_FILE_SIZE_BYTES) {
        return latestBlob;
      }
    }

    if (!latestBlob) {
      throw new Error('Failed to prepare the cropped image.');
    }

    return latestBlob;
  }

  private canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Failed to export the cropped image.'));
          return;
        }
        resolve(blob);
      }, 'image/jpeg', quality);
    });
  }

  private buildCroppedFileName(originalName: string | undefined): string {
    const name = `${originalName ?? ''}`.trim();
    const rawBaseName = name.replace(/\.[^.]+$/, '').trim() || 'renter-image';
    const sanitizedBaseName = rawBaseName
      .normalize('NFKD')
      .replace(/[^\x20-\x7E]/g, '')
      .replace(/[^a-zA-Z0-9._ -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^[.\-_\s]+|[.\-_\s]+$/g, '')
      .slice(0, 80);
    const finalBaseName = sanitizedBaseName || 'renter-image';
    return `${finalBaseName}-cropped.jpg`;
  }

  private clampNumber(value: number, min: number, max: number): number {
    if (!Number.isFinite(value)) {
      return min;
    }
    return Math.min(Math.max(value, min), max);
  }
}
